/**
 * The look of the create flow (redesign, Bez 2026-10-09): a header with the vault and the FREE mark, a numbered stepper on a line, the kind
 * of lock as two big cards with V3 positions one row below, a "sealed until" card for the date, vesting behind a switch, a sealed summary
 * for the review and equal Back / Continue twins. Display only: nothing here reads the chain or builds a transaction.
 */
import styled from 'styled-components'
import { Icon } from '../../TokenPages/icons'
import { DISPLAY, MONO } from '../ui'

export const Head = styled.header`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  display: grid;
  grid-template-columns: 76px minmax(0, 1fr);
  grid-template-areas: 'vault title' 'free free';
  gap: 14px 14px;
  align-items: center;
  padding: 16px;
  border-radius: 24px;
  border: 1px solid rgba(127, 212, 232, 0.26);
  background: radial-gradient(90% 140% at 15% 0%, rgba(33, 0, 255, 0.3) 0%, rgba(126, 0, 254, 0.1) 40%, transparent 72%),
    linear-gradient(180deg, #060a1f 0%, #0a1030 100%);
  box-shadow: inset 0 1px 0 rgba(223, 233, 255, 0.12), 0 18px 44px rgba(0, 0, 0, 0.45);
  .vault {
    grid-area: vault;
  }
  .title {
    grid-area: title;
    display: grid;
    gap: 4px;
    min-width: 0;
  }
  .free {
    grid-area: free;
    min-width: 0;
  }
  h1 {
    margin: 0;
    font-family: ${DISPLAY};
    font-weight: 800;
    font-size: 30px;
    line-height: 34px;
    letter-spacing: -0.02em;
    color: #f4f6ff;
  }
  p {
    margin: 0;
    font-size: 15px;
    line-height: 22px;
    color: #c9d2e6;
  }
  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: 104px minmax(0, 1fr) auto;
    grid-template-areas: 'vault title free';
    gap: 20px;
    padding: 20px 28px 20px 20px;
    h1 {
      font-size: 40px;
      line-height: 44px;
    }
  }
`

export const Stepper = styled.ol`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin: 0;
  padding: 0;
  list-style: none;
  li {
    position: relative;
    display: grid;
    justify-items: center;
    gap: 6px;
    min-width: 0;
    text-align: center;
    font-size: 13px;
    line-height: 18px;
    font-weight: 700;
    color: #aab6dc;
  }
  /* the line from the previous step, lit once that step is done */
  li + li::before {
    content: '';
    position: absolute;
    z-index: 0;
    top: 17px;
    right: calc(50% + 22px);
    width: calc(100% - 44px);
    height: 2px;
    border-radius: 2px;
    background: rgba(180, 200, 255, 0.2);
  }
  li[data-done='true'] + li::before,
  li[data-done='true'] + li[aria-current='step']::before {
    background: #7fd4e8;
  }
  .dot {
    position: relative;
    z-index: 1;
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    font-family: ${MONO};
    font-size: 14px;
    font-weight: 500;
    color: #c9d2e6;
    background: #0a0e22;
    border: 2px solid rgba(180, 200, 255, 0.28);
  }
  li[data-done='true'] .dot {
    color: #7fd4e8;
    border-color: #7fd4e8;
    background: rgba(127, 212, 232, 0.12);
  }
  li[aria-current='step'] {
    color: #f4f6ff;
  }
  li[aria-current='step'] .dot {
    color: #050713;
    background: #7fd4e8;
    border-color: #7fd4e8;
    box-shadow: 0 0 0 5px rgba(127, 212, 232, 0.16), 0 0 22px rgba(127, 212, 232, 0.45);
  }
`

export function Steps({ labels, step }: { labels: string[]; step: number }) {
  return (
    <Stepper aria-label="Steps">
      {labels.map((s, i) => (
        <li key={s} aria-current={i === step ? 'step' : undefined} data-done={i < step ? 'true' : 'false'}>
          <span className="dot">{i < step ? <Icon name="check" size={18} strokeWidth={2.6} /> : i + 1}</span>
          <span>{s}</span>
        </li>
      ))}
    </Stepper>
  )
}

/** the two main kinds of lock, side by side and equal */
export const KindGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
`
export const KindCard = styled.button<{ $on: boolean }>`
  position: relative;
  display: grid;
  align-content: start;
  gap: 4px;
  min-width: 0;
  min-height: 132px;
  padding: 14px;
  border-radius: 18px;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: #f4f6ff;
  background: linear-gradient(
    180deg,
    ${({ $on }) => ($on ? 'rgba(127, 212, 232, 0.16)' : 'rgba(159, 182, 255, 0.06)')} 0%,
    rgba(5, 7, 19, 0.6) 100%
  );
  border: ${({ $on }) => ($on ? '2px solid #7fd4e8' : '1px solid rgba(180, 200, 255, 0.22)')};
  box-shadow: inset 0 1px 0 rgba(223, 233, 255, 0.1)
    ${({ $on }) => ($on ? ', 0 0 0 4px rgba(127, 212, 232, 0.1), 0 10px 28px rgba(127, 212, 232, 0.14)' : '')};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    border-color: #7fd4e8;
  }
  &:focus-visible {
    outline: 2px solid #7fd4e8;
    outline-offset: 3px;
  }
  b {
    margin-top: 6px;
    font-family: ${DISPLAY};
    font-weight: 800;
    font-size: 19px;
    line-height: 24px;
  }
  small {
    font-size: 13px;
    line-height: 18px;
    color: #c9d2e6;
  }
  .tick {
    position: absolute;
    top: 12px;
    right: 12px;
    display: grid;
    place-items: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    color: #050713;
    background: #7fd4e8;
    visibility: ${({ $on }) => ($on ? 'visible' : 'hidden')};
  }
  ${({ theme }) => theme.mediaQueries.md} {
    padding: 18px;
    b {
      font-size: 22px;
      line-height: 28px;
    }
    small {
      font-size: 14px;
      line-height: 20px;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`
/** the third kind, a quieter full-width row: V3 positions are one tap away, not the first thing asked */
export const KindRow = styled.button<{ $on: boolean }>`
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) 26px;
  gap: 12px;
  align-items: center;
  width: 100%;
  min-height: 60px;
  padding: 8px 14px 8px 10px;
  border-radius: 16px;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: #f4f6ff;
  background: ${({ $on }) => ($on ? 'rgba(127, 212, 232, 0.14)' : 'rgba(5, 7, 19, 0.5)')};
  border: ${({ $on }) => ($on ? '2px solid #7fd4e8' : '1px dashed rgba(180, 200, 255, 0.3)')};
  &:hover {
    border-color: #7fd4e8;
  }
  &:focus-visible {
    outline: 2px solid #7fd4e8;
    outline-offset: 3px;
  }
  b {
    display: block;
    font-weight: 800;
    font-size: 15px;
    line-height: 20px;
  }
  small {
    display: block;
    font-size: 13px;
    line-height: 18px;
    color: #c9d2e6;
  }
  .ring {
    display: grid;
    place-items: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    color: #050713;
    border: 2px solid ${({ $on }) => ($on ? '#7fd4e8' : 'rgba(180, 200, 255, 0.4)')};
    background: ${({ $on }) => ($on ? '#7fd4e8' : 'transparent')};
  }
`

/** the date the lock opens, said once and big: the heart of the "How long" step */
export const Until = styled.div<{ $ok: boolean }>`
  display: grid;
  gap: 4px;
  padding: 14px 16px;
  border-radius: 16px;
  background: ${({ $ok }) =>
    $ok ? 'linear-gradient(90deg, rgba(127, 212, 232, 0.12), rgba(5, 7, 19, 0.4))' : 'rgba(255, 107, 122, 0.08)'};
  border: 1px solid ${({ $ok }) => ($ok ? 'rgba(127, 212, 232, 0.4)' : 'rgba(255, 107, 122, 0.4)')};
  color: ${({ $ok }) => ($ok ? '#c9d2e6' : '#ffc9cf')};
  font-size: 15px;
  line-height: 22px;
  small {
    font-size: 12px;
    line-height: 16px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #7fd4e8;
  }
  b {
    font-family: ${MONO};
    font-weight: 500;
    font-size: 18px;
    line-height: 26px;
    color: #f4f6ff;
  }
  ${({ theme }) => theme.mediaQueries.md} {
    b {
      font-size: 24px;
      line-height: 32px;
    }
  }
`
export const PresetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  > button {
    justify-items: center;
    align-content: center;
    text-align: center;
  }
  > button[aria-pressed='true'] {
    color: #f4f6ff;
    box-shadow: inset 0 0 0 1px #7fd4e8, 0 0 0 3px rgba(127, 212, 232, 0.12);
  }
  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
`

/** an on/off row for an optional part (vesting): the checkbox drawn as a switch, the label says what it adds */
export const Switch = styled.label`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 52px;
  gap: 12px;
  align-items: center;
  min-height: 60px;
  padding: 10px 14px;
  border-radius: 16px;
  cursor: pointer;
  background: rgba(5, 7, 19, 0.5);
  border: 1px solid rgba(180, 200, 255, 0.2);
  b {
    display: block;
    font-size: 15px;
    line-height: 20px;
    font-weight: 800;
    color: #f4f6ff;
  }
  small {
    display: block;
    font-size: 13px;
    line-height: 18px;
    color: #c9d2e6;
  }
  input {
    appearance: none;
    -webkit-appearance: none;
    position: relative;
    width: 52px;
    height: 30px;
    margin: 0;
    border-radius: 999px;
    cursor: pointer;
    background: rgba(180, 200, 255, 0.2);
    border: 1px solid rgba(180, 200, 255, 0.35);
    transition: background 0.15s ease;
  }
  input::after {
    content: '';
    position: absolute;
    top: 3px;
    left: 3px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #dfe9ff;
    transition: transform 0.15s ease;
  }
  input:checked {
    background: #7fd4e8;
    border-color: #7fd4e8;
  }
  input:checked::after {
    transform: translateX(22px);
    background: #050713;
  }
  input:focus-visible {
    outline: 2px solid #7fd4e8;
    outline-offset: 3px;
  }
  @media (prefers-reduced-motion: reduce) {
    input,
    input::after {
      transition: none;
    }
  }
`

/** a quiet "add more" row that opens one optional field */
export const MoreBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-self: start;
  text-align: left;
  min-height: 44px;
  padding: 0 4px;
  border: 0;
  cursor: pointer;
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  color: #7fd4e8;
  background: transparent;
  &:focus-visible {
    outline: 2px solid #7fd4e8;
    outline-offset: 2px;
    border-radius: 8px;
  }
`

/** the review: a sealed summary, the vault beside the facts */
export const Summary = styled.div`
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  gap: 14px;
  align-items: start;
  padding: 14px;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(127, 212, 232, 0.08), rgba(5, 7, 19, 0.5));
  border: 1px solid rgba(127, 212, 232, 0.3);
  dl {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
    margin: 0;
  }
  dl > div {
    display: grid;
    gap: 2px;
    padding: 8px 0;
    border-top: 1px solid rgba(180, 200, 255, 0.14);
  }
  dl > div:first-child {
    border-top: 0;
    padding-top: 0;
  }
  dt {
    font-size: 12px;
    line-height: 16px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #aab6dc;
  }
  dd {
    margin: 0;
    min-width: 0;
    font-size: 16px;
    line-height: 22px;
    color: #f4f6ff;
    overflow-wrap: anywhere;
  }
  dd.free {
    font-family: ${DISPLAY};
    font-weight: 900;
    font-size: 20px;
    text-transform: uppercase;
    color: #f7cf62;
  }
  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: 112px minmax(0, 1fr);
    gap: 20px;
    padding: 18px 20px;
    dl > div {
      grid-template-columns: 140px minmax(0, 1fr);
      gap: 16px;
      align-items: baseline;
    }
  }
`
export const Pledge = styled.p`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin: 0;
  font-size: 15px;
  line-height: 22px;
  color: #dfe9ff;
  svg {
    flex: none;
    margin-top: 2px;
    color: #7fd4e8;
  }
`
/** the one transaction the flow is for: the brand gradient, a lock, a little glow */
export const LockGo = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-height: 58px;
  padding: 0 20px;
  border: 0;
  border-radius: 16px;
  cursor: pointer;
  font-family: ${DISPLAY};
  font-weight: 800;
  font-size: 18px;
  color: #f4f6ff;
  background: linear-gradient(180deg, #2100ff 0%, #7e00fe 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.25), 0 10px 28px rgba(33, 0, 255, 0.4);
  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
    box-shadow: none;
  }
  &:focus-visible {
    outline: 2px solid #7fd4e8;
    outline-offset: 3px;
  }
`
/** Back and Continue: two equal halves, the slot of a missing one kept */
export const Twins = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  > button {
    width: 100%;
  }
  ${({ theme }) => theme.mediaQueries.md} {
    max-width: 520px;
  }
`
